/**
 * AI Module - Clarifier Output Schema
 *
 * Zod schema for validating the Clarifier pass output.
 * The Clarifier (Haiku) checks if the Fact Pack has sufficient data
 * to generate a useful journey plan.
 *
 * Output determines:
 * - canProceed: true → proceed to Planner
 * - canProceed: false → show clarifying questions to user
 */

import { z } from 'zod';

// ============================================
// Clarifying Question Schema
// ============================================

export const clarifyingQuestionSchema = z.object({
  id: z.string(),
  field: z.string(), // Which Fact Pack field this relates to
  question: z.string(), // User-friendly question text
  why: z.string(), // Why we need this information
  options: z.array(z.string()).optional(), // Optional multiple choice options
});

// ============================================
// Main Clarifier Output Schema
// ============================================

export const clarifierOutputSchema = z.object({
  canProceed: z.boolean(),
  clarifyingQuestions: z.array(clarifyingQuestionSchema).max(3), // Max 3 questions
  proceedWithCaveats: z.array(z.string()), // Caveats if proceeding with assumptions
});

// ============================================
// Type Exports
// ============================================

export type ClarifierOutput = z.infer<typeof clarifierOutputSchema>;
export type ClarifyingQuestion = z.infer<typeof clarifyingQuestionSchema>;

// ============================================
// Validation Helpers
// ============================================

/**
 * Validate clarifier output
 */
export function validateClarifierOutput(data: unknown): {
  valid: boolean;
  data?: ClarifierOutput;
  errors?: string[];
} {
  const result = clarifierOutputSchema.safeParse(data);

  if (result.success) {
    return { valid: true, data: result.data };
  }

  return {
    valid: false,
    errors: result.error.issues.map(
      (issue) => `${issue.path.join('.')}: ${issue.message}`
    ),
  };
}

/**
 * Create a default "can proceed" response
 * Used when no blocking fields exist in Fact Pack
 */
export function createDefaultCanProceed(): ClarifierOutput {
  return {
    canProceed: true,
    clarifyingQuestions: [],
    proceedWithCaveats: [],
  };
}

/**
 * Create a "needs clarification" response
 */
export function createNeedsClarification(
  questions: Array<{
    id: string;
    field: string;
    question: string;
    why: string;
    options?: string[];
  }>
): ClarifierOutput {
  return {
    canProceed: false,
    clarifyingQuestions: questions.slice(0, 3), // Max 3 questions
    proceedWithCaveats: [],
  };
}

/**
 * Check if clarifier result indicates we need user input
 */
export function needsUserInput(result: ClarifierOutput): boolean {
  return !result.canProceed && result.clarifyingQuestions.length > 0;
}

/**
 * Check if clarifier result has caveats to show
 */
export function hasCaveats(result: ClarifierOutput): boolean {
  return result.proceedWithCaveats.length > 0;
}
